import React from "react";
import { Link } from "react-router-dom";
import { usePageBootstrap } from "../../hooks/useBootstrapList";
import { useBootstrapWS } from "../../hooks/useBootstrapWS";
import { PageHeader, KpiTile, SectionCard } from "../../components/Ui";
import { ClipboardCheck, ArrowRight } from "lucide-react";
import { Button } from "../../components/ui/button";

export default function StoreKeeperDashboard() {
  const { data, reload } = usePageBootstrap({
    path: "/dashboard/bootstrap",
    wsTypes: ["store_grn", "gate_pass"],
  });
  useBootstrapWS(["store_grn", "gate_pass"], reload);

  const grnQueue = data?.grn_queue_preview || [];
  const tareQueue = data?.tare_queue_preview || [];

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="store-keeper-dashboard">
      <PageHeader
        eyebrow="Store & GRN"
        title="Store Keeper Desk"
        description="Receive general procurement trucks — inspect lines, post GRN, then send to weighbridge for tare."
        actions={
          <Button asChild className="bg-bansal-sage hover:bg-bansal-sageDark text-white min-h-11">
            <Link to="/store/grn">
              <ClipboardCheck className="h-4 w-4 mr-2" />
              Open store GRN
            </Link>
          </Button>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        <KpiTile
          label="Awaiting GRN"
          value={data?.awaiting_grn_count ?? 0}
          sub="In yard · store inward"
          accent
          testId="store-kpi-grn"
        />
        <KpiTile
          label="Tare pending"
          value={data?.tare_pending_count ?? 0}
          sub="After GRN at bridge"
          testId="store-kpi-tare"
        />
        <KpiTile
          label="Gross pending"
          value={data?.gross_pending_count ?? 0}
          sub="Weigh loaded truck"
          testId="store-kpi-gross"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <SectionCard title="Awaiting store GRN" testId="store-grn-queue">
          {grnQueue.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No trucks waiting for GRN.</p>
          ) : (
            <div className="space-y-2">
              {grnQueue.map((row) => (
                <Link
                  key={row.gate_pass_id || row.id}
                  to={`/store/grn?gate_pass=${row.gate_pass_id}`}
                  className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-3 py-3 hover:bg-muted/40 transition-colors"
                  data-testid={`store-grn-${row.gate_pass_id}`}
                >
                  <div className="min-w-0">
                    <div className="font-mono text-sm uppercase text-foreground">{row.vehicle_number || "—"}</div>
                    <div className="text-sm truncate text-muted-foreground">
                      {row.party_name || row.supplier_name || "—"}
                    </div>
                    <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
                      GP {row.gate_pass_code || row.gate_pass_id}
                    </div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground shrink-0">
                    {row.stage?.replace(/_/g, " ") || "GRN"}
                  </span>
                </Link>
              ))}
            </div>
          )}
          <Link to="/store/grn" className="inline-flex items-center gap-1 text-xs link-brand mt-3">
            Open GRN intake <ArrowRight className="h-3 w-3" />
          </Link>
        </SectionCard>

        <SectionCard title="Tare weigh pending" testId="store-tare-queue">
          {tareQueue.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No trucks awaiting tare after GRN.</p>
          ) : (
            <div className="space-y-2">
              {tareQueue.map((row) => (
                <Link
                  key={`tare-${row.gate_pass_id}`}
                  to="/weighbridge"
                  className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-3 py-3 hover:bg-muted/40 transition-colors"
                >
                  <div className="min-w-0">
                    <div className="font-mono text-sm uppercase text-foreground">{row.vehicle_number || "—"}</div>
                    <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
                      GP {row.gate_pass_code || row.gate_pass_id}
                    </div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-300 shrink-0">
                    Tare
                  </span>
                </Link>
              ))}
            </div>
          )}
          <Link to="/weighbridge" className="inline-flex items-center gap-1 text-xs link-brand mt-3">
            Open weighbridge <ArrowRight className="h-3 w-3" />
          </Link>
        </SectionCard>
      </div>
    </div>
  );
}
